import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import Dialog from "@material-ui/core/Dialog";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import withCustomStyles from "./GFeedback.style";
import GButton from "./GButton";

// Empty state: icon disc, one-line title, one line of help, one action.
export const GEmpty = withCustomStyles(
  ({ classes, className, icon, title, children, action }) => (
    <div className={classnames(classes.empty, className)}>
      {icon && (
        <span className={classes.emptyDisc} aria-hidden="true">
          {icon}
        </span>
      )}
      <h2 className={classes.emptyTitle}>{title}</h2>
      {children && <p className={classes.emptyText}>{children}</p>}
      {action && <div className={classes.emptyAction}>{action}</div>}
    </div>
  )
);

GEmpty.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node.isRequired,
  action: PropTypes.node
};

// Inline notice: tint background + icon. info (orange), error (red), success (green).
export const GNotice = withCustomStyles(
  ({ classes, className, tone, icon, title, children, action }) => (
    <div
      className={classnames(classes.notice, classes[tone], className)}
      role={tone === "error" ? "alert" : undefined}
    >
      {icon && (
        <span className={classes.noticeIcon} aria-hidden="true">
          {icon}
        </span>
      )}
      <div className={classes.noticeBody}>
        {title && <strong className={classes.noticeTitle}>{title}</strong>}
        {children && <span>{children}</span>}
      </div>
      {action}
    </div>
  )
);

GNotice.propTypes = {
  tone: PropTypes.oneOf(["info", "error", "success"]),
  icon: PropTypes.node,
  title: PropTypes.node,
  action: PropTypes.node
};

GNotice.defaultProps = { tone: "info" };

// Dialog: radius 24, max 480px. Becomes a bottom sheet on phones.
// Pass `confirmLabel` + `onConfirm` for the standard two-button layout;
// `destructive` turns the confirm button red (only for confirmed deletes).
const GDialogBase = ({
  classes,
  open,
  onClose,
  title,
  children,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  destructive,
  confirmDisabled,
  actions
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(
    `(max-width:${theme.breakpoints.values.sm - 0.05}px)`
  );
  const titleId = React.useMemo(
    () =>
      `gdialog-${Math.random()
        .toString(36)
        .slice(2, 8)}`,
    []
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={title ? titleId : undefined}
      classes={{
        container: isPhone ? classes.sheetContainer : undefined,
        paper: classnames(classes.paper, { [classes.sheet]: isPhone })
      }}
    >
      {title && (
        <h2 id={titleId} className={classes.dialogTitle}>
          {title}
        </h2>
      )}
      {children && <div className={classes.dialogBody}>{children}</div>}
      {(actions || confirmLabel) && (
        <div className={classes.dialogActions}>
          {actions || (
            <>
              {cancelLabel && (
                <GButton variant="secondary" onClick={onCancel || onClose}>
                  {cancelLabel}
                </GButton>
              )}
              <GButton
                variant={destructive ? "destructive" : "primary"}
                onClick={onConfirm}
                disabled={confirmDisabled}
              >
                {confirmLabel}
              </GButton>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
};

export const GDialog = withCustomStyles(GDialogBase);

GDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.node,
  confirmLabel: PropTypes.node,
  cancelLabel: PropTypes.node,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  destructive: PropTypes.bool,
  confirmDisabled: PropTypes.bool,
  actions: PropTypes.node
};

GDialog.defaultProps = {
  cancelLabel: "Cancel",
  destructive: false,
  confirmDisabled: false
};
